import React from 'react';
import { Container, Typography, Paper, Box, Grid, Button, Card, CardContent, CircularProgress, Divider } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable'; // İzin ikonu
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Onay ikonu
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'; // Takvim ikonu
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'; // Bakiye ikonu
import { useTranslation } from 'react-i18next';

const LeaveManagementPage: React.FC = () => {
  const { t } = useTranslation(['humanResources', 'common2']);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      overflowX: 'hidden'
    }}>
      <Box 
        sx={{ 
          height: 'calc(100vh - 80px)', 
          overflowY: 'auto', 
          overflowX: 'hidden',
          width: '100%',
          paddingRight: '10px',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            marginLeft: '10px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
            '&:hover': {
              background: '#555'
            }
          }
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ pb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <EventAvailableIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                {hr('pages.leave.title', 'Leave Management System')}
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
              {hr('pages.leave.subtitle', 'This page is under construction. The leave management module will be available soon.')}
            </Typography>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.developmentProcess', 'Development Process')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ my: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.analysis', 'Analysis')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={100} color="success" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.completed', 'Completed')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.design', 'Design')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={40} color="primary" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.inProgress', 'In Progress')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.development', 'Development')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={10} color="warning" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.initialStage', 'Initial Stage')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.plannedFeatures', 'Module Features (Planned)')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <EventAvailableIcon sx={{ fontSize: 40, mb: 1, color: 'info.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.leave.features.createRequestTitle', 'Create Leave Request')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.leave.features.createRequestDesc', 'Allows employees to easily choose leave type and create requests by selecting date ranges.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <CheckCircleOutlineIcon sx={{ fontSize: 40, mb: 1, color: 'success.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.leave.features.approvalTitle', 'Approval Workflow Management')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.leave.features.approvalDesc', 'Workflows for managers and HR to review, approve, or reject leave requests.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <CalendarMonthIcon sx={{ fontSize: 40, mb: 1, color: 'secondary.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.leave.features.calendarTitle', 'Calendar View and Balance Tracking')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.leave.features.calendarDesc', 'Enables users to view their own leave balances and the company-wide leave calendar.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: 'rgba(63, 81, 181, 0.04)' }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.leave.whyTitle', 'Why a Leave Management System?')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body1" paragraph>
                {hr('pages.leave.whyIntro', 'An effective leave management system is critical for businesses. With this system:')}
              </Typography>
              
              <Box component="ul" sx={{ pl: 2 }}>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.leave.benefits.confusionReduced', 'Confusion and errors in leave processes are reduced.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.leave.benefits.satisfaction', 'Employee satisfaction increases and transparency is ensured.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.leave.benefits.compliance', 'Compliance with legal regulations becomes easier.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.leave.benefits.workload', 'The workload of HR and managers is reduced.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.leave.benefits.planning', 'Workforce planning becomes more effective.')}</Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                {hr('pages.leave.conclusion', 'Our leave management module aims to digitize all these processes and provide an easy and efficient experience for both employees and management.')}
              </Typography>
            </Paper>

            <Box sx={{ textAlign: 'center', mt: 4, pb: 4 }}>
              <Button variant="contained" color="primary" disabled>
                {hr('pages.shared.moduleAvailableWhenReady', 'The Module Will Be Available When Ready')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LeaveManagementPage; 